import React from 'react';
import '../../components/Dashboard/Dashboard.styles.css';
import { ResponsiveBar } from '@nivo/bar';
import { Select } from '../ui';
import { Switch } from '@progress/kendo-react-inputs';
import { PaperStatus } from '../../store/paper-status/types';
import styled from 'styled-components';
import { ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';
import EmptyDashboard from './EmptyDashboard';
import { getPaperStatusName, getValidationStatusName } from '../../helpers/translations';

interface DashboardSubmissionsProps {
  dashboardState: DashboardState;
  roleSelect: JSX.Element;
  keywordById: { [key: number]: Keyword };
  paperStatusById: { [key: number]: PaperStatus };
  handleChangeTargetRole: (targetRoleId: string, areaId: string, paperStatusId: string) => void;
  roleId: string;
  areaId: string;
  paperStatusId: string;
}

const DashboardSubmissions: React.FC<DashboardSubmissionsProps> = ({
  dashboardState,
  roleSelect,
  keywordById,
  paperStatusById,
  handleChangeTargetRole,
  roleId,
  areaId,
  paperStatusId,
}) => {
  const { t, i18n } = useTranslation();
  const [inverseRatio, setInverseRatio] = React.useState<boolean>(false);
  const [scaleCheckSubmissions, setScaleCheckSubmissions] = React.useState<boolean>(false);
  const [scaleCheckReviewers, setScaleCheckReviewers] = React.useState<boolean>(false);
  const [scaleCheckRatios, setScaleCheckRatios] = React.useState<boolean>(false);

  const getLegendName = (field: 'ratio' | 'total_submissions' | 'total_reviewers' | string): string => {
    switch (field) {
      case 'ratio':
        return t('ratio');
      case 'total_reviewers':
        return t('total reviewers');
      case 'total_submissions':
        return t('total submissions');
      default:
        return field;
    }
  };

  const assignmentsPerReviewer = dashboardState.data.assignments_per_reviewer ?? [];
  const assignmentsPerSubmission = dashboardState.data.assignments_per_submission ?? [];
  const reviews_per_status = dashboardState.data.reviews_per_status ?? [];
  const submissions_and_reviewers = dashboardState.data.submissions_and_reviewers ?? [];

  const totalReviews = reviews_per_status.reduce((sum: number, status: { value: number }) => sum + status.value, 0);

  /*
  const submissionsAndReviewers = dashboardState.data.submissions_and_reviewers.map((item: any) => ({
    ...item,
    total_reviewers: Math.floor(Math.random() * 600) + 1,
  }));
  */

  const submissionsAndReviewersWithRatios = submissions_and_reviewers.map((item: any) => {
    let ratio;
    if (inverseRatio && item.total_submissions > 0) {
      ratio = item.total_reviewers / item.total_submissions;
    } else if (!inverseRatio && item.total_reviewers > 0) {
      ratio = item.total_submissions / item.total_reviewers;
    } else {
      ratio = 0;
    }
    return {
      ...item,
      ratio: ratio.toFixed(2),
    };
  });

  return (
    <>
      <div className="flex flex-row justify-end mb-12 w-full">
        <StyledSelectWrapper className="flex flex-row justify-end items-baseline">{roleSelect}</StyledSelectWrapper>

        <StyledSelectWrapper className="flex flex-row justify-end ml-5 items-baseline">
          <Select
            label={t('Area')}
            value={areaId}
            options={[{ id: '', label: t('Not selected') }].concat(
              Object.values(keywordById)
                .filter((keyword) => keyword.parent_id == null)
                .map((keyword) => ({ id: keyword.id.toString(), label: keyword.name })),
            )}
            onChange={(value) => {
              handleChangeTargetRole(roleId, value, paperStatusId);
            }}
          />
        </StyledSelectWrapper>

        <StyledSelectWrapper className="flex flex-row justify-end ml-5 items-baseline">
          <Select
            label={t('Paper status')}
            value={paperStatusId}
            options={[{ id: '', label: t('Not selected') }].concat(
              Object.values(paperStatusById).map((status) => ({
                id: status.id.toString(),
                label: getPaperStatusName(status.name, t),
              })),
            )}
            onChange={(value) => {
              handleChangeTargetRole(roleId, areaId, value);
            }}
          />
        </StyledSelectWrapper>
      </div>
      <div
        className="flex flex-row justify-between w-full assignmentsPerStatusChart"
        style={{ height: '32rem', minWidth: 540 }}
      >
        {/* ASSIGNMENTS PER STATUS */}
        <div className="relative bg-white rounded-lg shadow-md mr-4 p-8 w-6/12">
          <h2 className="font-bold text-xl text-center">{t('Review progress')}</h2>

          {Object.keys(reviews_per_status).length > 0 ? (
            <>
              <ResponsivePie
                data={(reviews_per_status as any[]).map((value) => {
                  // Just overwrite label with according translation
                  return { ...value, label: getValidationStatusName(value.id, t) };
                })}
                sortByValue={true}
                margin={{ top: 50, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.65}
                padAngle={1}
                cornerRadius={1}
                colors={['#5ca8f9', '#f9a80e', '#86e6fb']}
                activeOuterRadiusOffset={8}
                borderWidth={1}
                borderColor={{
                  from: 'color',
                  modifiers: [['darker', 0.5]],
                }}
                arcLinkLabel="label"
                arcLinkLabelsSkipAngle={15}
                arcLinkLabelsTextColor="#333333"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{
                  from: 'color',
                  modifiers: [['darker', 2]],
                }}
                tooltip={(point) => {
                  return (
                    <div className="text-sm bg-white px-2 py-1 shadow-md flex flex-row">
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: point.datum.color,
                          marginTop: '4px',
                          marginRight: '8px',
                        }}
                      ></div>
                      <div>
                        {point.datum.label}: <strong>{point.datum.value}</strong>
                      </div>
                    </div>
                  );
                }}
                layers={['arcs', 'arcLabels', 'arcLinkLabels']}
              />

              <StyledTotal>
                <span className="title">{totalReviews}</span>
                <span className="text">{t('reviews')}</span>
              </StyledTotal>
            </>
          ) : (
            <EmptyDashboard className={'mt-36'} />
          )}
        </div>

        {/* ASSIGNMENTS PER SUBMISSION */}
        <div className="bg-white rounded-lg shadow-md p-8 w-6/12 assignmentsPerSummisionChart">
          <h2 className="font-bold text-xl text-center">{t('Assignments per submission')}</h2>
          <div className="flex flex-row items-center justify-end">
            <Switch
              onChange={() => setScaleCheckSubmissions(!scaleCheckSubmissions)}
              onLabel={''}
              offLabel={''}
              defaultChecked={true}
            />
            <p className="ml-4 text-sm">{t('Log scale')}</p>
          </div>
          {Object.keys(assignmentsPerSubmission).length > 0 ? (
            <ResponsiveBar
              data={assignmentsPerSubmission}
              keys={['frequency']}
              indexBy="total_assignments"
              margin={{ top: 20, right: 50, bottom: 170, left: 60 }}
              padding={0.6}
              layout="vertical"
              valueScale={{ type: scaleCheckSubmissions ? 'linear' : 'symlog' }}
              indexScale={{ type: 'band', round: true }}
              colors="#63b3ff"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisBottom={{ format: (v) => `${v} ${v > 1 ? t('assignments') : t('assignment')}`, tickRotation: -34 }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 0,
                tickRotation: 0,
                tickValues: 4,
                legend: t('submissions'),
                legendPosition: 'middle',
                legendOffset: -50,
              }}
              labelSkipHeight={15}
              enableLabel={true}
              tooltip={(point) => {
                return (
                  <div className="text-sm flex flex-row">
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: point.color,
                        marginTop: '4px',
                        marginRight: '8px',
                      }}
                    ></div>
                    <div>
                      {point.value} {point.value > 1 ? t('submissions with') : t('submission with')} {point.indexValue}{' '}
                      {Number(point.indexValue) > 1 ? t('assignments') : t('assignment')}
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            <EmptyDashboard className={'mt-32'} />
          )}
        </div>
      </div>

      <div
        className="flex flex-row justify-between mt-12 w-full mb-4 assignmentsPerReviewerChart"
        style={{ height: '32rem', minWidth: 540 }}
      >
        {/* ASSIGNMENTS PER REVIEWER */}
        <div className="bg-white rounded-lg shadow-md p-8 w-full">
          <h2 className="font-bold text-xl text-center">{t('Assignments per reviewer')}</h2>
          <div className="flex flex-row items-center justify-end">
            <Switch
              onChange={() => setScaleCheckReviewers(!scaleCheckReviewers)}
              onLabel={''}
              offLabel={''}
              defaultChecked={true}
            />
            <p className="ml-4 text-sm">{t('Log scale')}</p>
          </div>
          {Object.keys(assignmentsPerReviewer).length > 0 ? (
            <ResponsiveBar
              data={assignmentsPerReviewer}
              keys={['frequency']}
              indexBy="total_assignments"
              margin={{ top: 20, right: 50, bottom: 150, left: 60 }}
              padding={0.6}
              valueScale={{ type: scaleCheckReviewers ? 'linear' : 'symlog' }}
              indexScale={{ type: 'band', round: true }}
              colors="#f9a80e"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{ format: (v) => `${v} ${v > 1 ? t('assignments') : t('assignment')}`, tickRotation: -34 }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 0,
                tickRotation: 0,
                tickValues: 4,
                legend: t('reviewers'),
                legendPosition: 'middle',
                legendOffset: -50,
              }}
              labelSkipHeight={15}
              enableLabel={true}
              tooltip={(point) => {
                return (
                  <div className="text-sm flex flex-row">
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: point.color,
                        marginTop: '4px',
                        marginRight: '8px',
                      }}
                    ></div>
                    <div>
                      {point.value} {point.value > 1 ? t('reviewers with') : t('reviewer with')} {point.indexValue}{' '}
                      {Number(point.indexValue) > 1 ? t('assignments') : t('assignment')}
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            <EmptyDashboard className={'mt-36'} />
          )}
        </div>
      </div>

      {Object.keys(keywordById).length > 0 && (
        <div
          className="flex flex-row justify-around gap-4 mt-8 mb-12 w-full submissionsRatiosChart"
          style={{ height: '36rem', minWidth: 540 }}
        >
          {/* SUBMISSIONS AND REVIEWERS RATIOS */}
          <div className="bg-white rounded-lg shadow-md p-8 w-full">
            <h2 className="font-bold text-xl text-center mb-1">{t('Submissions and reviewers ratios')}</h2>
            <div className="flex flex-row items-center gap-4 justify-end">
              <Switch
                onChange={() => setInverseRatio(!inverseRatio)}
                onLabel={''}
                offLabel={''}
                defaultChecked={true}
              />
              <p className="text-sm mr-6">
                {!inverseRatio ? t('Ratio: submissions / reviewers') : t('Ratio: reviewers / submissions')}
              </p>

              <Switch
                onChange={() => setScaleCheckRatios(!scaleCheckRatios)}
                onLabel={''}
                offLabel={''}
                defaultChecked={true}
              />
              <p className="text-sm">{t('Log scale')}</p>
            </div>
            {Object.keys(submissionsAndReviewersWithRatios).length > 0 ? (
              <ResponsiveBar
                data={(submissionsAndReviewersWithRatios as any[]).map((value) => {
                  // Apply translations...
                  const newVal: { [key: string]: any } = {};
                  for (const property in value) {
                    newVal[getLegendName(property)] = value[property];
                  }
                  return newVal;
                })}
                keys={[getLegendName('ratio'), getLegendName('total_submissions'), getLegendName('total_reviewers')]}
                indexBy="areaId"
                margin={{ top: 20, right: 150, bottom: 200, left: 60 }}
                padding={0.6}
                layout="vertical"
                valueScale={{ type: scaleCheckRatios ? 'linear' : 'symlog' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#ec6f6f', '#5ca8f9', '#86e6fb']}
                groupMode="grouped"
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisBottom={{
                  format: (v) => {
                    const text = Object.values(keywordById)
                      .filter((keyword) => keyword.id == v)
                      .map((keyword) => ({ id: keyword.id.toString(), label: keyword.name }.label.slice(0, 35)));

                    return text;
                  },
                  tickRotation: -34,
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 0,
                  tickRotation: 0,
                  tickValues: 4,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: -50,
                }}
                labelSkipHeight={15}
                enableLabel={false}
                tooltip={(point) => {
                  return (
                    <div className="text-sm flex flex-row">
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: point.color,
                          marginTop: '4px',
                          marginRight: '8px',
                        }}
                      ></div>
                      <div>
                        {point.value} {point.id}
                      </div>
                    </div>
                  );
                }}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 120,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 20,
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemOpacity: 1,
                        },
                      },
                    ],
                  },
                ]}
              />
            ) : (
              <EmptyDashboard className={'mt-36'} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardSubmissions;

const StyledSelectWrapper = styled.div`
  > div {
    margin-right: 0.85rem;
    min-width: inherit;
  }
`;

const StyledTotal = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  font-weight: 600;
  color: rgb(0, 0, 0);
  text-align: center;
  line-height: 1.1;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  background-color: #ffffff;
  color: #333333;

  .title {
    font-size: 2rem;
    margin-right: 0.5rem;
  }
  .text {
    font-size: 0.775rem;
  }
`;
